import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./utils";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as React from "react";

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.List>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
    <TabsPrimitive.List
        ref={ref}
        className={cn(
            "inline-flex justify-center items-center bg-indigo-100 dark:bg-slate-700 p-1 rounded-md text-indigo-500 dark:text-slate-300",
            className
        )}
        {...props}
    />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
    <TabsPrimitive.Trigger
        ref={ref}
        className={cn(
            "inline-flex justify-center items-center data-[state=active]:bg-white dark:data-[state=active]:bg-slate-950 disabled:opacity-50 data-[state=active]:shadow-sm px-3 py-1.5 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-950 dark:focus-visible:ring-slate-300 ring-offset-white focus-visible:ring-offset-2 dark:ring-offset-slate-900 dark:ring-offset-slate-950 font-medium data-[state=active]:text-indigo-950 dark:data-[state=active]:text-slate-50 text-sm whitespace-nowrap transition-all disabled:pointer-events-none",
            className
        )}
        {...props}
    />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
    <TabsPrimitive.Content
        ref={ref}
        className={cn(
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-950 dark:focus-visible:ring-indigo-300 ring-offset-white focus-visible:ring-offset-2 dark:ring-offset-indigo-950 dark:ring-offset-slate-900",
            className
        )}
        {...props}
    />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

const navTabsListVariants = cva("flex flex-wrap gap-1 border-slate-200 dark:border-slate-800 border-b w-full");

const navTabsLinkVariants = cva(
    "inline-flex items-center gap-2 -mb-px px-3 py-2.5 border-b-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 font-medium text-sm whitespace-nowrap transition-colors",
    {
        variants: {
            variant: {
                default: "",
                indigo: "",
                amber: "",
                cyan: "",
            },
            active: {
                true: "",
                false: "border-transparent text-muted-foreground hover:text-foreground hover:border-slate-300 dark:hover:border-slate-600",
            },
        },
        compoundVariants: [
            {
                variant: "default",
                active: true,
                className: "border-indigo-600 text-indigo-700 dark:text-indigo-400",
            },
            {
                variant: "indigo",
                active: true,
                className: "border-indigo-600 text-indigo-800 dark:text-indigo-400",
            },
            {
                variant: "amber",
                active: true,
                className: "border-amber-600 text-amber-800 dark:text-amber-400",
            },
            {
                variant: "cyan",
                active: true,
                className: "border-cyan-600 text-cyan-800 dark:text-cyan-400",
            },
        ],
        defaultVariants: {
            variant: "default",
            active: false,
        },
    }
);

type NavTabsVariant = NonNullable<VariantProps<typeof navTabsLinkVariants>["variant"]>;

const NavTabsVariantContext = React.createContext<NavTabsVariant>("default");

const NavTabs = React.forwardRef<
    HTMLElement,
    React.ComponentPropsWithoutRef<"nav"> & {
        "aria-label": string;
    }
>(({ className, ...props }, ref) => <nav ref={ref} className={cn("w-full min-w-0", className)} {...props} />);
NavTabs.displayName = "NavTabs";

interface NavTabsListProps extends React.ComponentPropsWithoutRef<"div">, VariantProps<typeof navTabsLinkVariants> {}

const NavTabsList = React.forwardRef<HTMLDivElement, NavTabsListProps>(
    ({ className, variant = "default", ...props }, ref) => (
        <NavTabsVariantContext.Provider value={variant ?? "default"}>
            <div ref={ref} className={cn(navTabsListVariants(), className)} {...props} />
        </NavTabsVariantContext.Provider>
    )
);
NavTabsList.displayName = "NavTabsList";

interface NavTabsLinkProps extends React.ComponentPropsWithoutRef<"a">, VariantProps<typeof navTabsLinkVariants> {
    asChild?: boolean;
    active?: boolean;
}

const NavTabsLink = React.forwardRef<HTMLAnchorElement, NavTabsLinkProps>(
    ({ className, variant: variantProp, active = false, asChild = false, ...props }, ref) => {
        const contextVariant = React.useContext(NavTabsVariantContext);
        const variant = variantProp ?? contextVariant;
        const Comp = asChild ? Slot : "a";

        return (
            <Comp
                ref={ref}
                aria-current={active ? "page" : undefined}
                className={cn(navTabsLinkVariants({ variant, active }), className)}
                {...props}
            />
        );
    }
);
NavTabsLink.displayName = "NavTabsLink";

export {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
    NavTabs,
    NavTabsList,
    NavTabsLink,
    navTabsLinkVariants,
    navTabsListVariants,
};
export type { NavTabsVariant };
